import { ErrorState } from '@repo/design-system-kit';
import cx from 'clsx';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { Mollie } from '../../../types/mollie';
import { Footer } from '../../shared';
import { Basket } from '../basket';
import { CheckoutFeedback } from '../checkout-feedback';
import { CheckoutForm, type CheckoutData } from '../checkout-form';
import type { PaymentLink } from '../types';
import styles from './checkout-page.module.css';

interface CheckoutPageProps {
  paymentLink: PaymentLink;
  mollie?: Mollie | null;
  onCheckout?: ({ email, method, token }: CheckoutData) => void;
  hasNewPayment?: boolean;
  isError?: boolean;
  hasMollieError?: boolean;
  isPolling?: boolean;
  onRetry?: () => void;
  onMollieErrorRetry?: () => void;
  isApplePayAvailable?: boolean;
  disabled?: boolean;
}

export function CheckoutPage({
  paymentLink,
  mollie,
  onCheckout,
  hasNewPayment = false,
  hasMollieError = false,
  isError = false,
  isPolling = false,
  onRetry,
  onMollieErrorRetry,
  isApplePayAvailable = false,
  disabled = false,
}: CheckoutPageProps): ReactNode {
  const { formatMessage } = useIntl();
  const { amount, items, organizationName, potentialPaymentMethods, status } = paymentLink;

  if (hasMollieError) {
    return (
      <main className={cx(styles.container, styles.error)}>
        <ErrorState
          onRetry={onMollieErrorRetry}
          subtitle={formatMessage({ id: 'error-states.mollie-activation.legend' })}
          title={formatMessage({ id: 'error-states.mollie-activation.title' })}
        />
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <div className={styles.basket}>
        <Basket amount={amount} items={items || []} payee={organizationName} />
      </div>
      <div className={styles.form}>
        {hasNewPayment || status === 'paid' ? (
          <CheckoutFeedback
            hasNewPayment={hasNewPayment}
            isError={isError}
            isPolling={isPolling}
            onRetry={onRetry}
            payee={organizationName}
          />
        ) : (
          <CheckoutForm
            amount={amount}
            disabled={disabled}
            isApplePayAvailable={isApplePayAvailable}
            methods={potentialPaymentMethods}
            mollie={mollie}
            onCheckout={onCheckout}
            payee={organizationName}
          />
        )}
        <Footer className={styles['desktop-footer']} hasPaymentLink />
      </div>
      <Footer className={styles['mobile-footer']} hasPaymentLink />
    </main>
  );
}
